import { navigate } from '@reach/router';
import React, { Component } from 'react';
import { presets, spring, TransitionMotion } from 'react-motion';
import { Grid, Hidden } from '@material-ui/core';

import { WrapperSize } from '../../contrib/ui';

import { ValueType } from 'react-select/lib/types';
import AutoComplete from '../AutoComplete';
import Select from '../Select';

interface ITab {
  title: string;
  children: JSX.Element;
  url?: string;
}

interface ITabsProps {
  children: Array<React.ReactElement<ITab> | Array<React.ReactElement<ITab>>>;
  onChange?: (tabIndex: number) => void;
  currentTab: string | number;
  extraNavigationElement?: JSX.Element;
  menuTitle?: string;
  useAutoComplete?: boolean;
  outlinedAutoComplete?: boolean;
}

interface OptionType {
  label: string;
  value: number | string;
  url?: string;
}

interface ITabsState {
  selected: number;
}

class FancyTabs extends Component<ITabsProps, ITabsState> {
  constructor(props: ITabsProps) {
    super(props);
    this.state = {
      selected: Number(props.currentTab) || 0
    };
    const { onChange = () => null } = props;
    onChange(this.state.selected);
  }

  public getDefaultStyles = () => {
    return React.Children.toArray(this.props.children).map((child, index) => ({
      data: child,
      key: `tab-${index}`,
      style: { left: 0, opacity: 1, position: 1 }
    }));
  };

  public getStyles = () => {
    const { selected } = this.state;
    return React.Children.toArray(this.props.children)
      .map((tab: any, index) => {
        return {
          data: tab,
          key: `tab-${index}`,
          style: {
            left: spring(0, presets.stiff),
            opacity: spring(1, presets.gentle),
            position: 1
          }
        };
      })
      .filter((_: any, index: number) => index === selected);
  };

  public willEnter(): any {
    return {
      left: WrapperSize,
      opacity: 1,
      position: 1
    };
  }

  public willLeave(): any {
    return {
      left: spring(-WrapperSize),
      opacity: spring(0),
      position: 0
    };
  }

  public select = (selected: ValueType<OptionType>) => {
    const value = (selected as OptionType).value;
    const url = (selected as OptionType).url;
    if (url) {
      navigate(url);
    }
    const selectedN = Number(value);
    if (selectedN !== this.state.selected) {
      const { onChange = () => null } = this.props;
      onChange(selectedN);
      this.setState({ selected: selectedN });
    }
  };

  public render(): JSX.Element {
    const {
      children,
      extraNavigationElement,
      menuTitle,
      outlinedAutoComplete,
      useAutoComplete
    } = this.props;

    const flatChildren = children.reduce((accumulator: any, child: any) => {
      if (Array.isArray(child)) {
        return accumulator.concat(child);
      } else {
        return accumulator.concat([child]);
      }
    }, []);

    const options = flatChildren.map((tab: any, index: number) => {
      return {
        label: tab.props.title,
        url: tab.props.url,
        value: String(index)
      };
    });

    return (
      <div className="fancy-tabs">
        <Grid className="navigator" container={true} spacing={8}>
          <Grid item={true} xs={12} md={6} lg={4}>
            {extraNavigationElement}
          </Grid>

          <Hidden mdDown={true}>
            <Grid item={true} xs={12} md={6} lg={4} />
          </Hidden>

          <Grid item={true} xs={12} md={6} lg={4}>
            <div
              className={`${extraNavigationElement ? 'select' : ''} ${
                !!useAutoComplete && outlinedAutoComplete ? 'outlinedAutoComplete' : ''
              }`}
            >
              {!!useAutoComplete ? (
                <AutoComplete
                  isSearchable={false}
                  placeholder={menuTitle}
                  className="select-autocomplete"
                  outlined={outlinedAutoComplete}
                  title={'SEASON'}
                  options={options}
                  onChange={this.select}
                />
              ) : (
                <Select
                  title={menuTitle || 'MENU'}
                  options={options}
                  onSelect={this.select}
                  defaultOption={options[this.props.currentTab]}
                />
              )}
            </div>
          </Grid>
        </Grid>

        <TransitionMotion
          defaultStyles={this.getDefaultStyles()}
          styles={this.getStyles()}
          willLeave={this.willLeave}
          willEnter={this.willEnter}
        >
          {styles => (
            <div className="fancy-wrapper">
              {styles.map(({ style, data }, key) => (
                <div
                  className="fancy-tab"
                  key={key}
                  style={{
                    opacity: style.opacity,
                    position: style.position === 1 ? 'relative' : 'absolute',
                    right: -style.left
                  }}
                >
                  {data.props.children}
                </div>
              ))}
            </div>
          )}
        </TransitionMotion>
      </div>
    );
  }
}

const Tab = ({ children }: ITab) => {
  return <div>{children}</div>;
};

export { FancyTabs, Tab };
